import './assets/main.css';
import 'shared/common/styles/datatable.css';

import { createApp, type App as VueApp } from 'vue';
import App from './App.vue';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import router from './router';
import { createPinia } from 'pinia';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import { getBasePath } from './common/api/basePathApi';
import { TiYardTheme } from 'shared/themes/tiyard';
import { SILogTheme } from 'shared/themes/silog';
import { getTheme } from 'shared/themes/api/themeApi';
import { useThemeStore, type Theme } from 'shared/themes/stores/themeStore'

async function initializeApp() {
  const basePath = await getBasePath();

  const head = document.querySelector('head');
  if (head) {
    const baseElement = document.createElement('base');
    baseElement.href = basePath;
    head.appendChild(baseElement);
  }

  const pinia = createPinia();
  const app = createApp(App);

  app.use(pinia);
  await setupPrimeVue(app); // needs to be after Pinia initiation.
  app.use(ConfirmationService);
  app.use(ToastService);

  // Initialize router with dynamic base path (after Pinia and PrimeVue)
  const routerInstance = router(basePath);
  app.use(routerInstance);
  app.directive('tooltip', Tooltip);

  app.mount('#app');
}

const THEME_ALIASES: Record<string, Theme> = {
  tiyard: 'TiYard',
  tilog: 'TiYard', // legacy name, kept for backwards compatibility with existing customers
  silog: 'SILog',
};

function toTheme(theme?: string | null): Theme {
  return THEME_ALIASES[theme?.toLowerCase() ?? ''] ?? 'SILog';
}

const PRESETS: Record<Theme, typeof SILogTheme> = {
  SILog: SILogTheme,
  TiYard: TiYardTheme,
};

async function setupPrimeVue(app: VueApp<Element>) {
  let theme: Theme = 'SILog';
  try {
    theme = toTheme(await getTheme());
  } catch (e) {
    console.error('Failed to load theme, using default', e);
  }

  useThemeStore().setTheme(theme);

  app.use(PrimeVue, {
    theme: {
      preset: PRESETS[theme],
      options: { darkModeSelector: '.p-dark' },
    },
    ripple: true,
  });
}

// Initialize the app
initializeApp();
